import { TeamBio, AppConfig } from '../types';
import { Camera, Film, Sparkles, Sliders } from 'lucide-react';
import { motion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { EditableText } from './EditableText';

interface DuoProfileProps {
  team: TeamBio[];
  isVisualEditActive?: boolean;
  onUpdateConfig?: (partialConfig: Partial<AppConfig>) => void;
}

export function DuoProfile({ team, isVisualEditActive = false, onUpdateConfig }: DuoProfileProps) {
  const { lang, t } = useLanguage();
  const aurelieIndex = team.findIndex(t => t.name.toLowerCase().includes('aurélie'));
  const yohannIndex = team.findIndex(t => t.name.toLowerCase().includes('yohann'));
  
  const yohann = yohannIndex >= 0 ? team[yohannIndex] : team[0];
  const aurelie = aurelieIndex >= 0 ? team[aurelieIndex] : team[1] || team[0];

  const yohannDesc = lang === 'en' && yohann?.description?.startsWith("Ancien monteur")
    ? "A former film editor for independent cinema, Yohann creates wedding films vibrant with life, paced like feature films. His approach relies on ambient sound, whispered vows, and bold cinematic angles."
    : yohann?.description || "";

  const aurelieDesc = lang === 'en' && aurelie?.description?.startsWith("Inspirée par la")
    ? "Inspired by editorial fashion photography, Aurélie looks for poetry in imperfection. She works exclusively with natural light and relies on digital precision to capture pure, intact emotions."
    : aurelie?.description || "";

  const handleSaveBio = (index: number, newDesc: string) => {
    if (index < 0 || !onUpdateConfig) return;
    const newTeam = [...team];
    newTeam[index] = { ...newTeam[index], description: newDesc };
    onUpdateConfig({ team: newTeam });
  };

  return (
    <section id="duo" className="py-24 bg-theme-bg border-t border-theme-border relative overflow-hidden">
      {/* Decorative Blur Backgrounds */}
      <div className="absolute top-1/2 left-0 w-80 h-80 rounded-full bg-theme-accent/5 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-96 h-96 rounded-full bg-theme-border/20 blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="text-xs uppercase tracking-[0.25em] text-theme-accent font-mono block mb-3">
            {t('duo_sub_title')}
          </span>
          <h2 className="text-3xl md:text-5xl font-mono text-theme-text font-extrabold tracking-tight mb-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.div
              animate={{ rotateY: 360 }}
              transition={{ repeat: Infinity, ease: "linear", duration: 8 }}
              style={{ perspective: 400, transformStyle: 'preserve-3d' }}
              className="w-12 h-12 md:w-16 md:h-16 text-theme-accent flex items-center justify-center flex-shrink-0"
            >
              {/* Elegant Wedding Bouquet SVG Standing Upright */}
              <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full stroke-theme-accent stroke-[1.5]">
                {/* Flowers Top (Round shape) */}
                <circle cx="32" cy="20" r="8" fill="none" className="stroke-theme-accent" />
                <circle cx="22" cy="24" r="6" fill="none" className="stroke-theme-accent" />
                <circle cx="42" cy="24" r="6" fill="none" className="stroke-theme-accent" />
                <circle cx="26" cy="14" r="5" fill="none" className="stroke-theme-accent" />
                <circle cx="38" cy="14" r="5" fill="none" className="stroke-theme-accent" />
                
                {/* Petals detail */}
                <path d="M32 16C32 18.5 30 20 28 20" />
                <path d="M32 24C34 24 36 22 36 20" />
                <path d="M22 24C22 26 24 26 25 24" />
                <path d="M42 24C42 22 40 22 39 24" />

                {/* Stems tying together */}
                <path d="M26 30 L29 45" />
                <path d="M32 28 L32 46" />
                <path d="M38 30 L35 45" />

                {/* Ribbon wrapped in the middle */}
                <rect x="27" y="36" width="10" height="4" rx="1" fill="none" className="stroke-theme-accent" />
                {/* Ribbon bow loop details */}
                <path d="M27 38 C24 38 23 35 25 34 C27 33 27 38 27 38 Z" />
                <path d="M37 38 C40 38 41 35 39 34 C37 33 37 38 37 38 Z" />
                {/* Ribbon tail hanging down */}
                <path d="M30 40 L28 48" />
                <path d="M34 40 L36 49" />
              </svg>
            </motion.div>
            <span>{t('duo_title')}</span>
          </h2>
          <p className="text-sm text-theme-secondary max-w-2xl mx-auto leading-relaxed">
            {t('duo_desc')}
          </p>
        </div>

        {/* Dynamic Duo Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-stretch">
          
          {/* Yohann - Vidéaste */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col md:flex-row gap-8 bg-theme-card border border-theme-border p-8 relative group hover:border-theme-accent/25 transition-all duration-500"
          >
            {/* Visual Frame */}
            <div className="w-full md:w-48 h-64 md:h-auto overflow-hidden relative flex-shrink-0">
              <img
                src={yohann.avatarUrl || undefined}
                alt={yohann.name}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-theme-bg via-transparent to-transparent opacity-60" />
              <div className="absolute bottom-3 left-3 bg-theme-bg/85 backdrop-blur-md px-3 py-1 border border-theme-border flex items-center gap-1">
                <Film className="w-3.5 h-3.5 text-theme-accent" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-theme-secondary">
                  {lang === 'fr' ? 'Vidéaste' : 'Videographer'}
                </span>
              </div>
            </div>

            {/* Biography & Gear Details */}
            <div className="flex flex-col justify-between py-2">
              <div>
                <span className="text-xs font-mono text-theme-accent uppercase tracking-widest mb-1.5 block">
                  {lang === 'fr' ? 'Le Récit en Mouvement' : 'Narrative in Motion'}
                </span>
                <h3 className="text-2xl font-serif text-theme-text font-bold mb-4">{yohann.name}</h3>
                <p className="text-sm text-theme-secondary leading-relaxed mb-6 font-light">
                  <EditableText
                    value={yohannDesc}
                    onSave={(val) => handleSaveBio(yohannIndex >= 0 ? yohannIndex : 0, val)}
                    isEditingMode={isVisualEditActive}
                    label="Présentation de Yohann"
                    multiline
                  >
                    <span>{yohannDesc}</span>
                  </EditableText>
                </p>
              </div>

              {/* Technical Spec List */}
              <div className="pt-4 border-t border-theme-border">
                <p className="text-[10px] font-mono text-theme-secondary uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <Sliders className="w-3.5 h-3.5 text-theme-accent" /> {t('duo_equip_vid')}
                </p>
                <div className="flex flex-wrap gap-2 font-mono">
                  {yohann.equipment && yohann.equipment.length > 0 ? (
                    yohann.equipment.map((eq, idx) => (
                      <span key={idx} className="px-2.5 py-1 bg-theme-bg border border-theme-border text-[9px] text-theme-secondary uppercase">{eq}</span>
                    ))
                  ) : (
                    <>
                      <span className="px-2.5 py-1 bg-theme-bg border border-theme-border text-[9px] text-theme-secondary uppercase">Sony FX3 Cinema</span>
                      <span className="px-2.5 py-1 bg-theme-bg border border-theme-border text-[9px] text-theme-secondary uppercase">Drone DJI Mavic 3 Pro Cine</span>
                      <span className="px-2.5 py-1 bg-theme-bg border border-theme-border text-[9px] text-theme-secondary uppercase">Optiques Cooke S7/i</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Aurélie - Photographe */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col md:flex-row gap-8 bg-theme-card border border-theme-border p-8 relative group hover:border-theme-accent/25 transition-all duration-500"
          >
            {/* Visual Frame */}
            <div className="w-full md:w-48 h-64 md:h-auto overflow-hidden relative flex-shrink-0">
              <img
                src={aurelie.avatarUrl || undefined}
                alt={aurelie.name}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-theme-bg via-transparent to-transparent opacity-60" />
              <div className="absolute bottom-3 left-3 bg-theme-bg/85 backdrop-blur-md px-3 py-1 border border-theme-border flex items-center gap-1">
                <Camera className="w-3.5 h-3.5 text-theme-accent" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-theme-secondary">
                  {lang === 'fr' ? 'Photographe' : 'Photographer'}
                </span>
              </div>
            </div>

            {/* Biography & Gear Details */}
            <div className="flex flex-col justify-between py-2">
              <div>
                <span className="text-xs font-mono text-theme-accent uppercase tracking-widest mb-1.5 block">
                  {lang === 'fr' ? "L'Instant Éternel" : "The Eternal Instant"}
                </span>
                <h3 className="text-2xl font-serif text-theme-text font-bold mb-4">{aurelie.name}</h3>
                <p className="text-sm text-theme-secondary leading-relaxed mb-6 font-light">
                  <EditableText
                    value={aurelieDesc}
                    onSave={(val) => handleSaveBio(aurelieIndex >= 0 ? aurelieIndex : 1, val)}
                    isEditingMode={isVisualEditActive}
                    label="Présentation d'Aurélie"
                    multiline
                  >
                    <span>{aurelieDesc}</span>
                  </EditableText>
                </p>
              </div>

              {/* Technical Spec List */}
              <div className="pt-4 border-t border-theme-border">
                <p className="text-[10px] font-mono text-theme-secondary uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <Sliders className="w-3.5 h-3.5 text-theme-accent" /> {t('duo_equip_pho')}
                </p>
                <div className="flex flex-wrap gap-2 font-mono">
                  {aurelie.equipment && aurelie.equipment.length > 0 ? (
                    aurelie.equipment.map((eq, idx) => (
                      <span key={idx} className="px-2.5 py-1 bg-theme-bg border border-theme-border text-[9px] text-theme-secondary uppercase">{eq}</span>
                    ))
                  ) : (
                    <>
                      <span className="px-2.5 py-1 bg-theme-bg border border-theme-border text-[9px] text-theme-secondary uppercase">Sony Alpha 5R</span>
                      <span className="px-2.5 py-1 bg-theme-bg border border-theme-border text-[9px] text-theme-secondary uppercase">Sony Alpha 7R V</span>
                      <span className="px-2.5 py-1 bg-theme-bg border border-theme-border text-[9px] text-theme-secondary uppercase">Leica M11-P</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Synergie Statement Box */}
        <div className="mt-16 bg-theme-card border border-theme-border p-8 text-center max-w-4xl mx-auto relative overflow-hidden">
          <div className="absolute inset-0 bg-theme-accent/[0.01] pointer-events-none" />
          <p className="text-theme-accent font-serif italic text-lg mb-2">"{t('duo_quote')}"</p>
          <p className="text-xs font-mono text-theme-secondary uppercase tracking-[0.2em] flex items-center justify-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-theme-accent animate-pulse" /> {t('duo_synergy')}
          </p>
        </div>

      </div>
    </section>
  );
}
